import { Lock } from 'lucide-react';
import { useState } from 'react';
import { DeviceFrame } from '@/components/app-editor/device-frame';
import { Skeleton } from '@/components/ui/skeleton';
import type { CreateWebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import { SiteBannerStaticPreview } from './site-banner-static-preview';

export interface SiteBannerCanvasProps {
  /** HTML of the backend preview (`useWebsiteBannerPreview`). */
  previewHtml: string | null;
  /** True while there is no preview to show yet (an older one stays visible while refreshing). */
  isPreviewLoading: boolean;
  previewFailed: boolean;
  /** Feeds the static fallback. */
  form: CreateWebsiteBannerDto;
  storeDomain: string;
  screenWidth: number;
  /** Tap on the banner: opens its tab (`chrome.focusTarget('content')`). */
  onBannerPress: () => void;
}

const FALLBACK_BANNER_HEIGHT_PX = 80;

/**
 * The store site on an iPhone: address bar, the banner preview (an iframe
 * without scripts, or the static fallback) and a simulated page below it.
 */
export function SiteBannerCanvas({
  previewHtml,
  isPreviewLoading,
  previewFailed,
  form,
  storeDomain,
  screenWidth,
  onBannerPress,
}: SiteBannerCanvasProps) {
  const { LL } = useI18nContext();
  const [bannerHeight, setBannerHeight] = useState(FALLBACK_BANNER_HEIGHT_PX);

  const showStatic =
    previewFailed || (!isPreviewLoading && previewHtml == null);

  return (
    <DeviceFrame platform="ios" screenWidth={screenWidth}>
      <div className="flex h-full w-full flex-col bg-white pt-9 text-neutral-900">
        <div className="mx-3 mb-2 flex items-center justify-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-[10px] text-neutral-600">
          <Lock aria-hidden className="size-2.5 shrink-0" />
          <span className="truncate">{storeDomain}</span>
        </div>
        <div className="relative">
          {showStatic ? (
            <SiteBannerStaticPreview
              content={form.content}
              theme={form.theme}
            />
          ) : previewHtml == null ? (
            <Skeleton
              aria-busy="true"
              className="w-full rounded-none"
              style={{ height: FALLBACK_BANNER_HEIGHT_PX }}
            />
          ) : (
            <iframe
              title={LL.websiteBannerPage.preview.bannerPreviewTitle()}
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              className="block w-full border-0"
              style={{ height: bannerHeight }}
              onLoad={(event) => {
                const height =
                  event.currentTarget.contentDocument?.documentElement
                    .scrollHeight;
                if (height) setBannerHeight(height);
              }}
            />
          )}
          <button
            type="button"
            aria-label={LL.websiteBannerEditor.canvas.editBanner()}
            className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            onClick={onBannerPress}
          />
        </div>
        <div className="mx-3 mt-3 h-16 rounded-lg bg-neutral-200" />
        <div className="mx-3 mt-3 grid min-h-0 flex-1 grid-cols-2 gap-2 overflow-hidden">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="space-y-1">
              <div className="aspect-square rounded-md bg-neutral-200" />
              <div className="h-2 w-3/4 rounded bg-neutral-200" />
            </div>
          ))}
        </div>
      </div>
    </DeviceFrame>
  );
}
